import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "../styles/PlanDetails.css";

/* ─── Sample data ─── */

const EMPLOYEE_CSV = `id,name,city
1,Swapnil,Pune
2,John,London
3,Sarah,New York`;

const EMPLOYEE_SQL = `CREATE TABLE employees (
    id INT,
    name VARCHAR(100),
    city VARCHAR(100)
);
INSERT INTO employees (id, name, city) VALUES
(1, 'Swapnil', 'Pune'),
(2, 'John', 'London'),
(3, 'Sarah', 'New York');`;

const HL7_EXCERPT = `MSH|^~\\&|…|ADT^A01
PID|1||P1001||John Doe
PV1|1|I|Room101`;

const PATIENT_JSON = `{
  "patient": {
    "patientId": "P1001",
    "name": "John Doe",
    "dateOfBirth": "1985-01-01",
    "gender": "M"
  }
}`;

const PATIENT_CSV = `patient_id,name,dob,gender
P1001,John Doe,1985-01-01,M`;

const EDI_EXCERPT = `ST*850*0001
BEG*00*SA*PO12345
N1*ST*ABC Company`;

const ORDER_JSON = `{
  "purchaseOrder": {
    "poNumber": "PO12345",
    "shipTo": "ABC Company"
  }
}`;

const ORDER_CSV = `po_number,ship_to
PO12345,ABC Company`;

/* ─── Plan data ─── */

const feature = (title, description, demo = null) => ({
  title,
  description,
  demo,
});

const PLAN_DETAILS = {
  free: {
    name: "Free",
    price: "₹0",
    annualPrice: "₹0",
    available: true,
    tagline: "Perfect for trying ConvertPro",
    description:
      "Try basic file conversion with common formats and a small daily allowance. A simple starting point before choosing a paid plan.",
    limits: [
      ["Conversions", "2/day", "Daily conversion allowance"],
      ["File size", "5 MB", "Maximum size per file"],
      ["History", "Last 5 conversions", "Conversion history included"],
    ],
    introduction:
      "The Free plan keeps your last 5 conversions and lets you explore basic conversion workflows before upgrading.",
    features: [
      feature(
        "Basic file conversion",
        "Change a file’s format using the basic conversion workflows available in ConvertPro."
      ),
      feature(
        "Standard file formats, including PDF & DOCX",
        "Convert common document, tabular, structured and text file formats, including PDF and DOCX."
      ),
      feature(
        "HL7 conversion",
        "HL7 is a healthcare data messaging format. HL7 conversion is included in the Free plan.",
        "hl7"
      ),
      feature(
        "History (last 5 conversions)",
        "Your history covers your five most recent conversions, rather than a fixed number of days."
      ),
    ],
  },

  basic: {
    name: "Basic",
    price: "₹99",
    annualPrice: "₹79",
    available: true,
    tagline: "Best for regular users",
    description:
      "For regular users who need daily conversions, CSV cleanup and structured data workflows with PDF and document support.",
    limits: [
      ["Conversions", "100/day", "Daily conversion allowance"],
      ["File size", "50 MB", "Maximum size per file"],
      ["History", "30 days", "Conversion history included"],
    ],
    introduction:
      "Basic gives you more room for everyday conversion work: 100 conversions per day, files up to 50 MB and 30 days of history.",
    features: [
      feature(
        "CSV cleaning & repair",
        "Tools for preparing CSV data for further use. Specific cleaning and repair options depend on the selected workflow."
      ),
      feature(
        "PDF & DOCX support",
        "Support for PDF documents and DOCX word-processing files. Select from the available operations for your document."
      ),
      feature(
        "CSV → SQL conversion",
        "Convert CSV data to SQL for structured data workflows. Review the generated result before using it in a database.",
        "sql"
      ),
      feature(
        "HL7 & EDI conversion",
        "HL7 is used for healthcare messages; EDI is used for electronic business data exchange. Conversion for both is included.",
        "messages"
      ),
    ],
  },

  standard: {
    name: "Standard",
    price: "₹499",
    annualPrice: "₹399",
    available: false,
    tagline: "Most popular for professionals",
    description:
      "For professionals who need batch processing, advanced CSV handling and Parquet or Avro support for bigger data workflows.",
    limits: [
      ["Conversions", "500/day", "Daily conversion allowance"],
      ["File size", "100 MB", "Maximum size per file"],
      ["History", "60 days", "Conversion history included"],
    ],
    introduction:
      "Standard is coming soon. Its planned features combine a larger daily allowance with tools for working with structured data and documents.",
    features: [
      feature(
        "Advanced CSV processing",
        "More advanced handling for CSV workflows. CSV cleaning is also included; specific processing options depend on the workflow."
      ),
      feature(
        "Smart SQL type detection",
        "Identify SQL data types such as integers and text. Review detected types before using the result."
      ),
      feature(
        "Parquet & Avro support",
        "Parquet is a column-oriented data format; Avro is a structured record format. Both are included for data workflows."
      ),
      feature(
        "Batch conversion",
        "Work with multiple files as a group rather than treating every file as a separate conversion task.",
        "batch"
      ),
      feature(
        "PDF extraction",
        "Extract information inside a PDF rather than simply changing its file format. Available outputs depend on the selected workflow.",
        "pdf"
      ),
    ],
  },

  premium: {
    name: "Premium",
    price: "₹999",
    annualPrice: "₹799",
    available: false,
    tagline: "Advanced data processing",
    description:
      "For advanced data processing with unlimited conversions, larger files and audio or video workflows alongside structured data formats.",
    limits: [
      ["Conversions", "Unlimited", "No conversion-count limit"],
      ["File size", "10 GB", "Maximum size per file"],
      ["History", "Unlimited", "Conversion history included"],
    ],
    introduction:
      "Premium is coming soon. The plan combines unlimited conversion history with files up to 10 GB and broader processing capabilities.",
    features: [
      feature(
        "Parquet, Avro, ORC",
        "Formats for structured datasets. Parquet and ORC store data by column; Avro represents structured records."
      ),
      feature(
        "Audio processing",
        "Audio workflows are included. Exact processing capabilities and outputs depend on the selected workflow."
      ),
      feature(
        "Video processing",
        "Video workflows are included. The supported operation depends on the workflow chosen for your file."
      ),
      feature(
        "Advanced extraction",
        "Work with information contained within a source file. Available extraction options depend on the file and workflow."
      ),
      feature(
        "Batch conversion & CSV cleaning",
        "Convert files as a group and prepare CSV data for further use."
      ),
      feature(
        "HL7 & EDI conversion",
        "Included support for healthcare messaging and electronic business data exchange."
      ),
    ],
    example: {
      input: "interview.mp4",
      output: "Processed output",
      description:
        "An illustrative video-processing workflow. The resulting content and format depend on the selected operation.",
    },
    closing: {
      title: "Premium is coming soon",
      description:
        "Purchase is not available yet. Review the planned capabilities before choosing your next plan.",
    },
  },

  enterprise: {
    name: "Enterprise",
    price: "₹1,999",
    annualPrice: "₹1,599",
    available: false,
    tagline: "For businesses & integrations",
    description:
      "For businesses that need API access, custom integrations, AI-powered workflows and priority support for large-scale conversion needs.",
    limits: [
      ["Conversions", "Unlimited", "No conversion-count limit"],
      ["File size", "Unlimited", "No file-size limit"],
      ["History", "Not specified", "No history limit has been published"],
    ],
    introduction:
      "Enterprise is coming soon. Unlimited conversions and file size sit alongside business integration capabilities.",
    features: [
      feature(
        "API access",
        "Work with ConvertPro programmatically through integration workflows."
      ),
      feature(
        "AI-powered extraction",
        "AI-assisted processing for extracting information from source content. Review results for your intended use."
      ),
      feature(
        "Audio/video insights",
        "Insights from audio and video content. Exact capabilities depend on the selected workflow."
      ),
      feature(
        "Custom integrations",
        "Connect ConvertPro with business workflows. Contact us to discuss your integration requirements."
      ),
      feature(
        "Priority support",
        "Priority support is included for business needs. No response-time commitment or support SLA has been specified."
      ),
      feature(
        "Batch conversion & CSV cleaning",
        "Convert files as a group and prepare CSV data for further use."
      ),
      feature(
        "Parquet & Avro support",
        "Included formats for structured data workflows: column-oriented Parquet and record-based Avro."
      ),
      feature(
        "HL7 & EDI conversion",
        "Conversion for healthcare messaging and electronic business data exchange."
      ),
    ],
    example: {
      input: "customer-call.mp4",
      output: "insights.json",
      description:
        "An illustrative audio/video insights workflow, not a promised output format or schema.",
    },
    closing: {
      title: "Discuss your business requirements",
      description:
        "Contact us for Enterprise enquiries. The plan is coming soon and cannot be purchased yet.",
    },
  },
};

/* ─── Shared page components ─── */

function Brand() {
  return (
    <Link to="/" className="pd-brand">
      <span className="pd-brand-mark" aria-hidden="true">⇄</span>
      ConvertPro
    </Link>
  );
}

function Navbar() {
  return (
    <header className="pd-navbar">
      <Brand />

      <nav className="pd-nav-links" aria-label="Main navigation">
        <Link to="/">Home</Link>
        <Link to="/feedback">Feedback</Link>
        <Link to="/pricing" className="pd-nav-active">Pricing</Link>
        <Link to="/contact">Contact</Link>
      </nav>

      <div className="pd-nav-actions">
        <Link to="/login">Sign In</Link>
        <Link to="/signup" className="pd-button">Get Started Free</Link>
      </div>
    </header>
  );
}

function PlanAction({ slug, available }) {
  if (slug === "free") {
    return (
      <Link to="/signup" className="pd-button">Get Started Free</Link>
    );
  }

  if (available) {
    // Reuse your existing pricing/payment flow.
    return (
      <Link to="/pricing" className="pd-button">Get Started</Link>
    );
  }

  return (
    <button type="button" className="pd-button pd-button-disabled" disabled>
      Coming Soon
    </button>
  );
}

function Footer() {
  return (
    <footer className="pd-footer">
      <Brand />
      <p>© 2026 ConvertPro. All rights reserved.</p>
    </footer>
  );
}

/* ─── Illustration components ─── */

function useReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);

    update();
    media.addEventListener("change", update);

    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

function Picture() {
  return (
    <svg
      className="pd-picture"
      viewBox="0 0 120 65"
      aria-hidden="true"
    >
      <rect width="120" height="65" rx="6" fill="#edb7a0" />
      <circle cx="27" cy="20" r="8" fill="#fff0cf" />
      <path d="M8 58 40 27 69 58Z" fill="#b77560" />
      <path d="M43 58 85 20 115 58Z" fill="#92513f" />
    </svg>
  );
}

function FileDocument({
  type,
  content,
  tone = "blue",
  compact = false,
  picture = false,
  children,
}) {
  return (
    <div
      className={`pd-document pd-document-${tone} ${
        compact ? "pd-document-compact" : ""
      }`}
    >
      <span className="pd-document-fold" aria-hidden="true" />
      <strong className="pd-document-type">{type}</strong>

      {picture && <Picture />}
      {content && <pre className="pd-document-data">{content}</pre>}
      {children}
    </div>
  );
}

function MiniFile({ type, tone, kind }) {
  return (
    <FileDocument type={type} tone={tone} compact>
      {kind === "image" ? (
        <Picture />
      ) : kind === "table" ? (
        <div className="pd-mini-table" aria-hidden="true">
          {Array.from({ length: 12 }, (_, index) => <i key={index} />)}
        </div>
      ) : (
        <div className="pd-mini-lines" aria-hidden="true">
          {Array.from({ length: 5 }, (_, index) => <i key={index} />)}
        </div>
      )}
    </FileDocument>
  );
}

function BatchFiles({ output = false }) {
  return (
    <div className="pd-batch-group">
      <div className="pd-batch-stack" aria-hidden="true">
        {[0, 1, 2].map((index) => (
          <div className="pd-batch-file" key={index}>
            <MiniFile
              type={output ? "SQL" : "CSV"}
              tone={output ? "green" : "blue"}
              kind={output ? "text" : "table"}
            />
          </div>
        ))}
      </div>
      <span className="pd-batch-count">
        {output ? "3 SQL files" : "3 CSV files"}
      </span>
    </div>
  );
}

function Converter() {
  return (
    <div className="pd-converter" aria-hidden="true">
      <span className="pd-converter-label">Converter App</span>
      <div className="pd-converter-shell">
        <div className="pd-converter-core">
          <span className="pd-converter-ring" />
          <span className="pd-converter-symbol">⇄</span>
        </div>
      </div>
    </div>
  );
}

function DemoInput({ kind }) {
  if (kind === "sql") {
    return <FileDocument type="CSV" content={EMPLOYEE_CSV} />;
  }

  if (kind === "hl7") {
    return <FileDocument type="HL7" content={HL7_EXCERPT} />;
  }

  if (kind === "edi") {
    return <FileDocument type="EDI" content={EDI_EXCERPT} />;
  }

  if (kind === "batch") {
    return <BatchFiles />;
  }

  return (
    <FileDocument
      type="PDF"
      picture
      content={`Quarterly report\n\nitem,qty\nBooks,12`}
    />
  );
}

function DemoOutput({ kind }) {
  if (kind === "sql") {
    return (
      <FileDocument
        type="SQL"
        content={EMPLOYEE_SQL}
        tone="green"
      />
    );
  }

  if (kind === "hl7" || kind === "edi") {
    const edi = kind === "edi";

    return (
      <div className="pd-split-output">
        <FileDocument
          type="JSON"
          content={edi ? ORDER_JSON : PATIENT_JSON}
          tone="green"
          compact
        />
        <FileDocument
          type="CSV"
          content={edi ? ORDER_CSV : PATIENT_CSV}
          tone="mint"
          compact
        />
      </div>
    );
  }

  if (kind === "batch") {
    return <BatchFiles output />;
  }

  return (
    <div className="pd-pdf-output">
      <MiniFile type="PNG" tone="peach" kind="image" />
      <MiniFile type="XLSX" tone="green" kind="table" />
      <MiniFile type="TXT" tone="purple" kind="text" />
    </div>
  );
}

function FeatureDemo({ type }) {
  const [paused, setPaused] = useState(false);
  const [messageKind, setMessageKind] = useState("edi");
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (type !== "messages" || paused || reducedMotion) return undefined;

    const timer = window.setInterval(() => {
      setMessageKind((current) => current === "edi" ? "hl7" : "edi");
    }, 10000);

    return () => window.clearInterval(timer);
  }, [type, paused, reducedMotion]);

  const kind = type === "messages" ? messageKind : type;

  const captions = {
    sql: "CSV → SQL · illustrative employee data",
    hl7: "HL7 → JSON + CSV · patient P1001 excerpt",
    edi: "EDI → JSON + CSV · PO12345 excerpt",
    batch: "Illustrative batch · three files converted together",
    pdf: "PDF → image, Excel and text · illustrative example",
  };

  return (
    <figure
      className={`pd-demo pd-demo-${kind} ${
        paused ? "pd-demo-paused" : ""
      }`}
      aria-label={captions[kind]}
    >
      {/* Changing the message kind restarts that ten-second illustration. */}
      <div className="pd-demo-stage" key={kind}>
        <div className="pd-demo-input">
          <DemoInput kind={kind} />
        </div>

        <Converter />

        <div className="pd-demo-output">
          <DemoOutput kind={kind} />
        </div>
      </div>

      <figcaption className="pd-demo-caption">
        <span>{captions[kind]}</span>

        {!reducedMotion && (
          <button
            type="button"
            onClick={() => setPaused((value) => !value)}
            aria-label={paused ? "Play illustration" : "Pause illustration"}
            aria-pressed={paused}
          >
            {paused ? "Play" : "Pause"}
          </button>
        )}
      </figcaption>
    </figure>
  );
}

function WorkflowExample({ example }) {
  return (
    <section className="pd-workflow">
      <p className="pd-eyebrow">ILLUSTRATIVE WORKFLOW</p>

      <div className="pd-workflow-flow">
        <div>
          <span>Example input</span>
          <strong>{example.input}</strong>
        </div>

        <span className="pd-workflow-arrow" aria-hidden="true">→</span>

        <div>
          <span>Example output</span>
          <strong>{example.output}</strong>
        </div>
      </div>

      <p>{example.description}</p>
      <small>
        Illustrative example only. Available operations depend on the
        selected workflow and file.
      </small>
    </section>
  );
}

/* ─── All five plan pages ─── */

export default function PlanDetails() {
  const { planSlug } = useParams();
  const slug = (planSlug || "").toLowerCase();
  const plan = PLAN_DETAILS[slug];

  if (!plan) {
    return (
      <div className="pd-page">
        <Navbar />
        <main className="pd-main">
          <h1>Plan not found</h1>
          <Link to="/pricing" className="pd-back-link">
            ← Back to Pricing
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pd-page">
      {/* Standard matches the latest design: no additional navigation bar. */}
      {slug !== "standard" && <Navbar />}

      <main className="pd-main">
        <Link to="/pricing" className="pd-back-link">
          <span aria-hidden="true">←</span> Back to Pricing
        </Link>

        <section className="pd-overview">
          <div className="pd-overview-copy">
            <div className="pd-heading-meta">
              <span className="pd-eyebrow">PLAN DETAILS</span>
              <span
                className={`pd-status ${
                  plan.available ? "pd-status-available" : ""
                }`}
              >
                {plan.available ? "Available now" : "Coming Soon"}
              </span>
            </div>

            <h1>{plan.name}</h1>
            <h2>{plan.tagline}</h2>
            <p>{plan.description}</p>
          </div>

          <aside className="pd-price-card" aria-label={`${plan.name} pricing`}>
            <span className="pd-eyebrow">MONTHLY PLAN</span>

            <div className="pd-price">
              <strong>{plan.price}</strong>
              <span>/month</span>
            </div>

            <p className="pd-annual-price">
              {plan.annualPrice}/month with annual billing
            </p>

            {slug === "free" && (
              <span className="pd-included">Included by Default</span>
            )}

            <PlanAction slug={slug} available={plan.available} />

            <small>
              {plan.available
                ? slug === "free"
                  ? "Sign up to start with the Free plan."
                  : "Available now. Get started with Basic."
                : "Purchase is disabled. This plan is not yet available."}
            </small>

            {slug === "enterprise" && (
              <Link to="/contact" className="pd-contact-link">
                Contact Us
              </Link>
            )}
          </aside>
        </section>

        <section className="pd-limits" aria-label="Plan limits">
          {plan.limits.map(([label, value, note]) => (
            <article className="pd-limit" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
              <p>{note}</p>
            </article>
          ))}
        </section>

        <section className="pd-features" aria-labelledby="pd-features-title">
          <header className="pd-section-header">
            <h2 id="pd-features-title">
              {plan.available
                ? `What ${plan.name} includes`
                : `What’s planned for ${plan.name}`}
            </h2>
            <p>{plan.introduction}</p>
          </header>

          <div className={`pd-feature-grid pd-feature-grid-${slug}`}>
            {plan.features.map((item) => (
              <article className="pd-feature-card" key={item.title}>
                <div className="pd-feature-heading">
                  <span className="pd-feature-icon" aria-hidden="true">✓</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </div>

                {item.demo && <FeatureDemo type={item.demo} />}
              </article>
            ))}
          </div>
        </section>

        {/* Only Premium and Enterprise retain these sections. */}
        {plan.example && <WorkflowExample example={plan.example} />}

        {plan.closing && (
          <section className="pd-closing">
            <div>
              <h2>{plan.closing.title}</h2>
              <p>{plan.closing.description}</p>
            </div>

            {slug === "enterprise" ? (
              <Link to="/contact" className="pd-button">Contact Us</Link>
            ) : (
              <PlanAction slug={slug} available={plan.available} />
            )}
          </section>
        )}
      </main>

      {/* One extreme-bottom footer, with no Get Started promotion. */}
      <Footer />
    </div>
  );
}
